import { useEffect, useMemo, useState } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, type PollHistoryRange, type PollHistoryResponse } from "@shared/api";
import { formatAgo } from "@shared/lib/time";
import styles from "./AdminPage.module.css";

const RANGES: PollHistoryRange[] = ["1h", "24h", "7d", "30d"];

const STATUS_LABELS: Record<string, string> = {
  match_inserted: "match inserted",
  no_match: "no match",
  duplicate_match: "duplicate",
  baseline: "baseline",
  reset: "reset",
  snapshot_skipped: "snapshot skipped",
  fetch_error: "fetch error",
  config_error: "config error",
};

const STATUS_COLORS: Record<string, string> = {
  match_inserted: "#3fb950",
  no_match: "#58a6ff",
  duplicate_match: "#8b949e",
  baseline: "#a371f7",
  reset: "#d29922",
  snapshot_skipped: "#db6d28",
  fetch_error: "#f85149",
  config_error: "#cf222e",
};

const STATUS_ORDER: string[] = [
  "match_inserted",
  "no_match",
  "duplicate_match",
  "baseline",
  "reset",
  "snapshot_skipped",
  "fetch_error",
  "config_error",
];

export function AdminPage() {
  const [range, setRange] = useState<PollHistoryRange>("24h");
  const [data, setData] = useState<PollHistoryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    api
      .pollHistory(range)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [range]);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <div className={styles.title}>Polling Health</div>
          <div className={styles.subtitle}>Cron status, success rate, and recent failures</div>
        </div>
        <a href="/" className={styles.backLink}>← Dashboard</a>
      </div>

      <div className={styles.rangeRow}>
        {RANGES.map((r) => (
          <button
            key={r}
            type="button"
            className={`${styles.rangeBtn} ${r === range ? styles.rangeBtnActive : ""}`}
            onClick={() => setRange(r)}
          >
            {r}
          </button>
        ))}
      </div>

      {error && <div className={styles.empty}>Error loading poll history: {error}</div>}
      {!error && !data && <div className={styles.empty}>Loading...</div>}
      {data && <AdminBody data={data} />}
    </div>
  );
}

function AdminBody({ data }: { data: PollHistoryResponse }) {
  const totals = data.totals;
  const successRatePct = totals.success_rate === null ? null : Math.round(totals.success_rate * 1000) / 10;
  const successRateClass = successRatePct === null
    ? ""
    : successRatePct >= 95
      ? styles.kpiValueGood
      : successRatePct >= 70
        ? styles.kpiValueWarn
        : styles.kpiValueBad;

  const lastSuccessAgo = data.last_success_at ? formatAgo(data.last_success_at) : "never";
  const lastFailureAgo = data.last_failure_at ? formatAgo(data.last_failure_at) : "never";
  const expectedPolls = Math.round(data.window_end - data.window_start) / (3 * 60);
  const coverage = expectedPolls > 0 ? Math.min(1, totals.polls / expectedPolls) : null;

  return (
    <>
      <div className={styles.kpis}>
        <Kpi
          label="Success rate"
          value={successRatePct === null ? "—" : `${successRatePct.toFixed(1)}%`}
          valueClass={successRateClass}
          sub={`${totals.successes} / ${totals.polls} polls`}
        />
        <Kpi
          label="Polls in window"
          value={String(totals.polls)}
          sub={coverage === null ? "" : `${Math.round(coverage * 100)}% of expected`}
        />
        <Kpi
          label="Failures"
          value={String(totals.failures)}
          valueClass={totals.failures > 0 ? styles.kpiValueBad : styles.kpiValueGood}
          sub={lastFailureLabel(data)}
        />
        <Kpi
          label="Last success"
          value={lastSuccessAgo}
          valueClass={data.last_success_at && data.now - data.last_success_at < 6 * 60 ? styles.kpiValueGood : styles.kpiValueWarn}
          sub={data.last_success_status ?? "—"}
        />
        <Kpi
          label="Last failure"
          value={lastFailureAgo}
          valueClass={data.last_failure_at && data.now - data.last_failure_at < 6 * 60 ? styles.kpiValueBad : ""}
          sub={data.last_failure_status ?? "—"}
        />
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>
          <span>Poll status over time</span>
          <span className={styles.sectionMeta}>
            bucket: {formatBucket(data.bucket_seconds)}
          </span>
        </div>
        <PollStatusChart data={data} />
        <PollLegend buckets={data.buckets} totals={totals.by_status} />
      </div>

      {data.last_failure_error && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            <span>Last failure detail</span>
            <span className={styles.sectionMeta}>{lastFailureAgo}</span>
          </div>
          <div className={styles.errorBox}>
            {data.last_failure_status}
            {data.last_failure_gametools_status !== null ? ` (gametools ${data.last_failure_gametools_status})` : ""}
            {"\n"}
            {data.last_failure_error}
          </div>
        </div>
      )}
    </>
  );
}

function Kpi({ label, value, valueClass, sub }: { label: string; value: string; valueClass?: string; sub?: string }) {
  return (
    <div className={styles.kpi}>
      <div className={styles.kpiLabel}>{label}</div>
      <div className={`${styles.kpiValue} ${valueClass ?? ""}`}>{value}</div>
      {sub && <div className={styles.kpiSub}>{sub}</div>}
    </div>
  );
}

function lastFailureLabel(data: PollHistoryResponse): string {
  if (data.totals.failures === 0) return "none in window";
  if (!data.last_failure_at) return "";
  return `most recent ${formatAgo(data.last_failure_at)}`;
}

interface ChartPoint {
  ts: number;
  [status: string]: number;
}

function PollStatusChart({ data }: { data: PollHistoryResponse }) {
  const presentStatuses = useMemo(() => {
    const set = new Set<string>();
    for (const b of data.buckets) for (const s of Object.keys(b.by_status)) set.add(s);
    return STATUS_ORDER.filter((s) => set.has(s));
  }, [data.buckets]);

  const chartData = useMemo<ChartPoint[]>(() => {
    return data.buckets.map((b) => {
      const point: ChartPoint = { ts: b.ts };
      for (const status of presentStatuses) point[status] = b.by_status[status] ?? 0;
      return point;
    });
  }, [data.buckets, presentStatuses]);

  if (chartData.length === 0 || data.totals.polls === 0) {
    return <div className={styles.empty}>No poll runs in this window.</div>;
  }

  return (
    <ResponsiveContainer width="100%" height={260}>
      <AreaChart data={chartData}>
        <CartesianGrid stroke="#30363d" strokeDasharray="3 3" />
        <XAxis
          dataKey="ts"
          stroke="#8b949e"
          tick={{ fontSize: 11 }}
          tickFormatter={(v) => formatTickByRange(v as number, data.range, data.bucket_seconds)}
          minTickGap={40}
        />
        <YAxis stroke="#8b949e" tick={{ fontSize: 11 }} width={32} allowDecimals={false} />
        <Tooltip
          contentStyle={{ background: "#161b22", border: "1px solid #30363d" }}
          labelFormatter={(v) => formatTooltipLabel(v as number, data.bucket_seconds)}
          formatter={(value: number, name: string) => [value, STATUS_LABELS[name] ?? name]}
        />
        {presentStatuses.map((status) => (
          <Area
            key={status}
            type="monotone"
            dataKey={status}
            stackId="1"
            stroke={STATUS_COLORS[status] ?? "#8b949e"}
            fill={STATUS_COLORS[status] ?? "#8b949e"}
            fillOpacity={0.7}
            isAnimationActive={false}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}

function PollLegend({ buckets, totals }: { buckets: PollHistoryResponse["buckets"]; totals: Record<string, number> }) {
  const present = useMemo(() => {
    const set = new Set<string>();
    for (const b of buckets) for (const s of Object.keys(b.by_status)) set.add(s);
    return STATUS_ORDER.filter((s) => set.has(s));
  }, [buckets]);

  if (present.length === 0) return null;

  return (
    <div className={styles.legend}>
      {present.map((status) => (
        <span key={status} className={styles.legendItem}>
          <span className={styles.legendSwatch} style={{ background: STATUS_COLORS[status] ?? "#8b949e" }} />
          {STATUS_LABELS[status] ?? status}
          {totals[status] !== undefined ? ` · ${totals[status]}` : ""}
        </span>
      ))}
    </div>
  );
}

function formatBucket(secs: number): string {
  if (secs < 60 * 60) return `${Math.round(secs / 60)} min`;
  if (secs < 24 * 60 * 60) return `${Math.round(secs / (60 * 60))} h`;
  return `${Math.round(secs / (24 * 60 * 60))} d`;
}

function formatTickByRange(unixSec: number, range: PollHistoryRange, bucketSec: number): string {
  const d = new Date(unixSec * 1000);
  if (bucketSec >= 24 * 60 * 60) {
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
  if (range === "1h") {
    return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  }
  return d.toLocaleTimeString(undefined, { hour: "numeric" });
}

function formatTooltipLabel(unixSec: number, bucketSec: number): string {
  const d = new Date(unixSec * 1000);
  if (bucketSec >= 24 * 60 * 60) return d.toLocaleDateString();
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
